import { Button, Stack, Tab, Typography, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Add } from '@mui/icons-material';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { Box } from '@mui/system';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Editor } from '../../../../../components/wysiwyg/Editor';
import { ActionsList } from '../../../../../opportunities/ActionsList';
import { useModalContext } from '../../../../../modal/ModalContext';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';
import { useSnackbar } from '../../../../../store/snackbar';

const opportunityDrawerTabs_getOpportunity = graphql(`
	query opportunityDrawerTabs_getOpportunity($opportunityId: UUID!) {
		getOpportunity(by: { id: $opportunityId }) {
			description
			actions {
				id
			}
		}
	}
`);

const opportunityDrawerTabs_updateOpportunity = graphql(`
	mutation opportunityDrawerTabs_updateOpportunity($opportunityId: UUID!, $data: OpportunityUpdateInput!) {
		updateOpportunity(by: { id: $opportunityId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

const useUpdateOpportunity = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	return useApiMutation(opportunityDrawerTabs_updateOpportunity, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateOpportunity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'opportunity.detail.update.success' }),
					error: intl.formatMessage({ id: 'opportunity.detail.update.error' }),
				},
				errorMessage: result.updateOpportunity.errorMessage,
				openSnackbar,
			}),
	});
};

type OpportunityDrawerFormProps = Readonly<{
	opportunityId: string;
	activityId?: string;
}>;

export const OpportunityDrawerTabs: FC<OpportunityDrawerFormProps> = ({ opportunityId, activityId }) => {
	const intl = useIntl();
	const theme = useTheme();
	const { setModal } = useModalContext();
	const [description, setDescription] = useState('');
	const [isEditorReady, setIsEditorReady] = useState(false);

	const { data: opportunity, isPending: isOpportunityPending } = useApiQuery(opportunityDrawerTabs_getOpportunity, {
		variables: { opportunityId },
	});

	const { mutate: updateDescription } = useUpdateOpportunity();

	useEffect(() => {
		if (!isOpportunityPending) {
			setDescription(opportunity?.getOpportunity?.description ?? '');
			setIsEditorReady(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpportunityPending]);

	useDebounceEffect(() => {
		if (isEditorReady && opportunity?.getOpportunity?.description !== description) {
			updateDescription({ opportunityId, data: { description } });
		}
	}, [isEditorReady, opportunity, description]);

	const [tab, setTab] = useState('1');

	const openAddActionModal = () => {
		setModal({ type: 'addAction', payload: { opportunityId, activityId } });
	};

	const getActionsCount = () => {
		const count = opportunity?.getOpportunity?.actions.length;
		if (count != null && count > 0) {
			return `(${count})`;
		}
		return '';
	};

	return (
		<TabContext value={tab}>
			<Box px={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<TabList onChange={(_, value) => setTab(value)}>
					<Tab
						label={intl.formatMessage({ id: 'journey.detail.widget.opportunities.tabs.details' })}
						value={'1'}
						sx={{ fontSize: 16, lineHeight: 1.5 }}
					></Tab>
					<Tab
						label={intl.formatMessage({ id: 'journey.detail.widget.opportunities.tabs.actions' }, { count: getActionsCount() })}
						value={'2'}
						sx={{ fontSize: 16, lineHeight: 1.5 }}
					></Tab>
				</TabList>
			</Box>
			<TabPanel value={'1'}>
				<Stack direction={'column'} gap={2.5}>
					<Typography variant={'h6'} color={theme.palette.grey[600]}>
						<FormattedMessage id={'journey.detail.widget.opportunities.description'} />
					</Typography>
					{isEditorReady && <Editor state={description} setState={setDescription} />}
				</Stack>
			</TabPanel>
			<TabPanel value={'2'}>
				<Stack direction={'column'} gap={3}>
					<ActionsList opportunityId={opportunityId} size={'small'} />
					<Box alignSelf={'flex-start'}>
						<Button type={'button'} onClick={openAddActionModal}>
							<Stack direction={'row'} gap={1} sx={{ color: theme.palette.secondary.main }} alignItems={'center'}>
								<Add sx={{ fontSize: 18 }} />
								<Typography variant={'h6'}>
									<FormattedMessage id={'journey.detail.widget.opportunities.add-action'} />
								</Typography>
							</Stack>
						</Button>
					</Box>
				</Stack>
			</TabPanel>
		</TabContext>
	);
};
